import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../../shared/api/axiosInstance';
// The four aggregate KPI endpoints are schema-first (bare model, no {data}
// envelope) via the shared substrate — import their generated types. The two AI
// residuals (daily-briefing, ai-crew-feed), the exception queue, and the v2b
// eta-changes / tracking-signals feeds keep their hand-written types. See
// docs/platform/dashboard-kpi-endpoints.md.
import type { ShipmentsCommandCenter } from '../../../generated/api/models/shipmentsCommandCenter';
import type { ShipmentsDashboard } from '../../../generated/api/models/shipmentsDashboard';
import type { ShipmentsDocumentCoverage } from '../../../generated/api/models/shipmentsDocumentCoverage';
import type { ShipmentsFxExposure } from '../../../generated/api/models/shipmentsFxExposure';
import type { ShipmentsInTransitMap } from '../../../generated/api/models/shipmentsInTransitMap';
import type { ShipmentsMissingDocs } from '../../../generated/api/models/shipmentsMissingDocs';
import type { ShipmentsNetworkBoard } from '../../../generated/api/models/shipmentsNetworkBoard';
import type {
  AiCrewFeed,
  DailyBriefing,
  EtaChangeFeed,
  ExceptionTriageQueue,
  TrackingSignals,
} from '../types';

const SHIPMENTS_API = '/api/v1/shipments';

export const shipmentsDashboardKeys = {
  all: ['shipments-dashboard'] as const,
  data: () => [...shipmentsDashboardKeys.all, 'data'] as const,
  commandCenter: () => [...shipmentsDashboardKeys.all, 'command-center'] as const,
  inTransitMap: () => [...shipmentsDashboardKeys.all, 'in-transit-map'] as const,
  exceptionTriage: () => [...shipmentsDashboardKeys.all, 'exception-triage'] as const,
  dailyBriefing: () => [...shipmentsDashboardKeys.all, 'daily-briefing'] as const,
  aiCrewFeed: () => [...shipmentsDashboardKeys.all, 'ai-crew-feed'] as const,
  networkBoard: () => [...shipmentsDashboardKeys.all, 'network-board'] as const,
  missingDocs: () => [...shipmentsDashboardKeys.all, 'missing-docs'] as const,
  documentCoverage: () => [...shipmentsDashboardKeys.all, 'document-coverage'] as const,
  fxExposure: () => [...shipmentsDashboardKeys.all, 'fx-exposure'] as const,
  etaChanges: () => [...shipmentsDashboardKeys.all, 'eta-changes'] as const,
  trackingSignals: () => [...shipmentsDashboardKeys.all, 'tracking-signals'] as const,
};

/** Single aggregate read powering the Shipments dashboard KPI widgets. */
export function useShipmentsDashboard() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.data(),
    queryFn: () => apiGet<ShipmentsDashboard>(`${SHIPMENTS_API}/dashboard/stats`),
    staleTime: 30 * 1000,
  });
}

/** In-transit shipments with a known current location (latest located actual
 *  tracking milestone) — powers the live map on the dashboard and list page. */
export function useInTransitMap() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.inTransitMap(),
    queryFn: () => apiGet<ShipmentsInTransitMap>(`${SHIPMENTS_API}/dashboard/in-transit-map`),
    staleTime: 30 * 1000,
  });
}

/** Aggregate read powering the forwarder command-center KPI-row and
 *  files-in-motion widgets (freight-persona home). */
export function useForwarderCommandCenter() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.commandCenter(),
    queryFn: () => apiGet<ShipmentsCommandCenter>(`${SHIPMENTS_API}/dashboard/command-center`),
    staleTime: 30 * 1000,
  });
}

/** The tenant-wide exception-triage queue (open exceptions, highest risk first)
 *  powering the command-center exception-triage widget. */
export function useExceptionTriageQueue() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.exceptionTriage(),
    queryFn: async () =>
      apiGet<ExceptionTriageQueue>(`${SHIPMENTS_API}/exceptions/queue`),
    staleTime: 30 * 1000,
  });
}

/** The Luma Daily Briefing (command-center v2 hero). The narrative is generated
 *  through the AI seam and cached per tenant/day server-side, so a longer
 *  staleTime here avoids re-fetching the same story on every home mount. */
export function useDailyBriefing() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.dailyBriefing(),
    queryFn: async () => {
      const response = await apiGet<{ data: DailyBriefing }>(
        `${SHIPMENTS_API}/dashboard/daily-briefing`
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** The AI-crew activity feed (recent intake items + drafted notifications). */
export function useAiCrewFeed() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.aiCrewFeed(),
    queryFn: async () => {
      const response = await apiGet<{ data: AiCrewFeed }>(
        `${SHIPMENTS_API}/dashboard/ai-crew-feed`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
  });
}

/** Active files still missing a checklist-expected document, with the next
 *  milestone gate they're due before — powers the missing-docs completeness
 *  widget on the module + home dashboards. */
export function useMissingDocs() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.missingDocs(),
    queryFn: () => apiGet<ShipmentsMissingDocs>(`${SHIPMENTS_API}/dashboard/missing-docs`),
    staleTime: 30 * 1000,
  });
}

/** The document-coverage matrix — every file in motion crossed with the document
 *  types its checklist calls for. Same backend read as `useMissingDocs` (one
 *  service), widened into the grid the tracker page renders. */
export function useDocumentCoverage() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.documentCoverage(),
    queryFn: () => apiGet<ShipmentsDocumentCoverage>(`${SHIPMENTS_API}/document-coverage`),
    staleTime: 30 * 1000,
  });
}

/** Total FX exposure by document currency across in-motion files — realized /
 *  unrealized currency gain-loss aggregated in one reporting base (P13), each row
 *  drilling through to the contributing files. Powers the control-tower FX
 *  exposure widget (forwarder persona; zeroed for a non-forwarder tenant). */
export function useFxExposure() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.fxExposure(),
    queryFn: () => apiGet<ShipmentsFxExposure>(`${SHIPMENTS_API}/dashboard/fx-exposure`),
    staleTime: 30 * 1000,
  });
}

/** The ETA-change diff feed — recent MATERIAL carrier-ETA revisions ("what moved
 *  since I last looked") + a per-viewer new-since-last-visit count (v2b F1). */
export function useEtaChanges() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.etaChanges(),
    queryFn: async () => {
      const response = await apiGet<{ data: EtaChangeFeed }>(
        `${SHIPMENTS_API}/dashboard/eta-changes`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
  });
}

/** Advance the viewer's ETA-change feed watermark (clears the "N new" badge). */
export function useMarkEtaChangesRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      apiPost<{ data: { last_seen_at: string; new_count: number } }>(
        `${SHIPMENTS_API}/dashboard/eta-changes/mark-read`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentsDashboardKeys.etaChanges() });
    },
  });
}

/** Open-exception KPI counts for the silent-shipment + rollover/transship tiles
 *  (v2b F2/F3). */
export function useTrackingSignals() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.trackingSignals(),
    queryFn: async () => {
      const response = await apiGet<{ data: TrackingSignals }>(
        `${SHIPMENTS_API}/dashboard/tracking-signals`
      );
      return response.data;
    },
    staleTime: 30 * 1000,
  });
}

/** The live network board (active files by lane + stage). Live-refreshed on
 *  `shipment.tracking_milestone` notifications by the widget. */
export function useNetworkBoard() {
  return useQuery({
    queryKey: shipmentsDashboardKeys.networkBoard(),
    queryFn: () => apiGet<ShipmentsNetworkBoard>(`${SHIPMENTS_API}/dashboard/network-board`),
    staleTime: 30 * 1000,
  });
}
